import type { FormikProps } from 'formik'
import type { FunctionComponent, ReactElement } from 'react'

import { trackingService } from '@monthio/react-components'
import { Formik } from 'formik'
import { useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'

import type { BudgetHelperQuestionnaireInputs } from '../../types'
import type { QuestionnaireStepKey } from './budgetHelperStateMachine'

import { budgetApiClient } from '../../api/budgetApiClient'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { goToFlow } from '../../store/actions/flowEntryStatemachineActions'
import { CivilStatus } from '../../types'
import { budgetHelperStateMachine } from './budgetHelperStateMachine'
import { StepKey, steps } from './steps/steps'

const InvalidState = () => <div>Invalid state</div>

export const QUESTIONNAIRE_INPUTS_STORAGE_KEY = 'MONTHIO:QUESTIONNAIRE_INPUTS'

const BudgetHelperForm: FunctionComponent<{
	formProps: FormikProps<BudgetHelperQuestionnaireInputs>
	onFormChanged: (value: BudgetHelperQuestionnaireInputs) => void
	children?: never
}> = ({ formProps, onFormChanged }): ReactElement => {
	const history = useHistory()

	const { step: routeStep } = useParams<{ step: StepKey }>()
	const step: QuestionnaireStepKey =
		routeStep === StepKey.Cohabiting
			? budgetHelperStateMachine.initial
			: (routeStep as QuestionnaireStepKey)

	const setCurrentStep = useCallback(
		(step: StepKey) => {
			const pathname = location.pathname.replace(/\/[^/]*$/, `/${step}`)
			history.push(pathname)
		},
		[history],
	)

	useEffect(() => {
		onFormChanged(formProps.values)
	}, [formProps.values, onFormChanged])

	const advanceStep = (choice: string) => {
		const nextStep = budgetHelperStateMachine.states[step]?.on?.[choice] ?? step

		if (nextStep == StepKey.End) {
			formProps.submitForm()
			return
		}
		setCurrentStep(nextStep)
	}

	const CurrentStep = steps[step] ?? InvalidState

	return <CurrentStep formProps={formProps} handleClick={advanceStep} />
}

export const BudgetHelper = (): ReactElement => {
	const dispatch = useDispatch()
	const initialValues: BudgetHelperQuestionnaireInputs = {
		civilStatus: CivilStatus.None,
		mergedFinances: null,
		spouseConsent: null,
		childrenInHousehold: null,
		childrenAges: [],
	}

	const [storedInputs, setStoredInputs] =
		useLocalStorage<BudgetHelperQuestionnaireInputs>(
			QUESTIONNAIRE_INPUTS_STORAGE_KEY,
			initialValues,
		)

	const onSubmit = async (values: BudgetHelperQuestionnaireInputs) => {
		const response = await budgetApiClient.createCaseWithSubdomainInfo(
			false,
			false,
			values,
			true,
		)
		const applicantId = response?.mainApplicantId
		if (applicantId != null) {
			trackingService.trackEvent('Finished budgetHelperLandingPage', {
				applicantId,
			})
			dispatch(goToFlow(applicantId))
		}
	}

	return (
		<Formik initialValues={storedInputs} onSubmit={onSubmit}>
			{(formProps) => (
				<BudgetHelperForm
					formProps={formProps}
					onFormChanged={setStoredInputs}
				/>
			)}
		</Formik>
	)
}
